//#region header */
/**************************************************************************************************
//
//  Description: Hook to post application names.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import useUserQueryStore from "../context/UserStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApplicationNameDto } from "../dtos/applicationNamesDto";
import SecurityAPIClient from "../services/securityApiClient";
import { saveApplicationDetails } from "../utils/ApplicationLookupStorage";

interface ApplicationModelPayload {
  name: string;
  displayname: string;
  dbConnectionNames: string[];
  elasticConnectionNames: string[];
}

interface PostApplicationNamesContext {
  previousApplicationNames: ApplicationNameDto[];
}

const usePostApplicationNames = (onUpdate: (newData: ApplicationNameDto[]) => void) => {
  const queryClient = useQueryClient();
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const securityApiClient = new SecurityAPIClient<ApplicationModelPayload | string>("/ApplicationName", currentToken!);

  return useMutation<
    (ApplicationModelPayload | string)[],
    Error,
    ApplicationNameDto[],
    PostApplicationNamesContext
  >({
    mutationFn: (newApplications) =>
      securityApiClient.post(
        newApplications.map((application) => ({
          name: application.name,
          displayname: application.name,
          dbConnectionNames: application.dbConnectionNames ?? [],
          elasticConnectionNames: application.elasticConnectionNames ?? [],
        }))
      ),
    onMutate: (newApplications) => {
      const previousApplications = queryClient.getQueryData<ApplicationNameDto[]>(["applicationNames"]) || [];
      queryClient.setQueryData<ApplicationNameDto[]>(["applicationNames"], () => [...newApplications]);

      return { previousApplicationNames: previousApplications };
    },
    onSuccess: (_savedApplications, newApplications) => {
      saveApplicationDetails(newApplications);
      queryClient.setQueryData<ApplicationNameDto[]>(["applicationNames"], newApplications);

      onUpdate(newApplications);
    },
    onError: (_error, _newApplications, context) => {
      if (!context) return;

      queryClient.setQueryData<ApplicationNameDto[]>(["applicationNames"], context.previousApplicationNames);
    },
  });
};

export default usePostApplicationNames;
